import React, { useState, useEffect, useCallback } from 'react';
import { fromIso, toIso } from '../utils/businessDate';
import styled from 'styled-components';
import axios from 'axios';
import { getTenantToday } from '../utils/dateUtils';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, 
  Calendar, 
  Shield, 
  FileText, 
  AlertCircle, 
  CheckCircle2, 
  Activity, 
  Zap, 
  Bot, 
  Sparkles, 
  Download, 
  RefreshCw, 
  Search,
  Filter,
  TrendingUp,
  FileCheck,
  Eye,
  X,
  Send,
  Ban,
  Check
} from 'lucide-react';
import { useRole } from '../hooks/useRole';
import { canGenerateRegulatoryReports, canViewSensitiveData } from '../utils/roleUtils';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import { cn } from '../lib/utils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';

import { DateField } from './shared/DateField';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> = 
  ({ label, value, icon, color, subText }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

const Card = styled(GlassCard)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  color: #f1f5f9;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #f1f5f9;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
`;

const Td = styled.td`
  padding: 12px;
  color: #e2e8f0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  font-size: 0.875rem;
`;

const Badge = styled.span<{ type: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.type) {
      case 'DRAFT':
        return 'background: rgba(234, 179, 8, 0.2); color: #facc15;';
      case 'PENDING_REVIEW':
        return 'background: rgba(251, 146, 60, 0.2); color: #fb923c;';
      case 'SUBMITTED':
        return 'background: rgba(59, 130, 246, 0.2); color: #60a5fa;';
      case 'ACCEPTED':
        return 'background: rgba(16, 185, 129, 0.2); color: #34d399;';
      case 'true':
        return 'background: rgba(16, 185, 129, 0.2); color: #34d399;';
      case 'false':
        return 'background: rgba(239, 68, 68, 0.2); color: #f87171;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return `
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        `;
      case 'secondary':
        return `
          background: rgba(148, 163, 184, 0.2);
          color: #e2e8f0;
          border: 1px solid rgba(148, 163, 184, 0.3);
        `;
      default:
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
        `;
    }
  }}
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  color: #94a3b8;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartContainer = styled.div`
  height: 250px;
  margin-top: 1rem;
`;

const DeadlineCard = styled.div<{ urgent?: boolean }>`
  background: ${props => props.urgent ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  border: 1px solid ${props => props.urgent ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)'};
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const DeadlineTitle = styled.div`
  font-weight: 600;
  color: #f1f5f9;
  margin-bottom: 4px;
`;

const DeadlineDate = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const DeadlineDays = styled.div<{ urgent?: boolean }>`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${props => props.urgent ? '#f87171' : '#60a5fa'};
  margin-top: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.4);
  border-radius: 6px;
  color: #60a5fa;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 8px;
  
  &:hover {
    background: rgba(59, 130, 246, 0.3);
  }
`;

// Interfaces
interface Filing {
  id: number;
  filingPeriod: string;
  periodEndDate: string;
  filingDate: string | null;
  status: string;
  totalValue: number;
  totalPositions: number;
  managerName: string;
  createdAt: string;
}

interface Holding {
  id: number;
  cusip: string;
  issuerName: string;
  classTitle: string;
  value: number;
  sharesOrPrincipal: number;
}

interface Deadline {
  type: string;
  period: string;
  deadline: string;
  daysRemaining: number;
}

interface DashboardData {
  total13FFilings: number;
  pending13FFilings: number;
  recent13FFilings: Filing[];
  totalFormPFFilings: number;
  pendingFormPFFilings: number;
  recentFormPFFilings: any[];
  totalFormADVFilings: number;
  pendingFormADVFilings: number;
  recentFormADVFilings: any[];
  totalExecutions30d: number;
  bestExecutionRate30d: number;
  unreportedMiFIDCount: number;
  upcomingDeadlines: Deadline[];
  overdueFilings: any[];
  overdueCount: number;
}

const RegulatoryReportingScreen: React.FC = () => {
  const { userRole } = useRole();
  const { isRetail } = useTenantConfig();
  const canGenerate = canGenerateRegulatoryReports(userRole);
  const canViewSensitive = canViewSensitiveData(userRole);
  const isReadOnly = userRole === 'USER';

  // Retail tenants only see Form ADV (RIA registration), not institutional forms
  const showInstitutionalForms = !isRetail;
  
  const [activeTab, setActiveTab] = useState<'dashboard' | '13f' | 'form-pf' | 'form-adv' | 'best-execution' | 'mifid' | 'ai-assistant'>('dashboard');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [filings, setFilings] = useState<Filing[]>([]);
  const [selectedFiling, setSelectedFiling] = useState<Filing | null>(null);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form 13F generation form
  const [filingPeriod, setFilingPeriod] = useState('');
  const [periodEndDate, setPeriodEndDate] = useState('');
  
  // Form PF generation state
  const [pfGroups, setPfGroups] = useState<any[]>([]);
  const [pfPortfolios, setPfPortfolios] = useState<any[]>([]);
  const [selectedPfTarget, setSelectedPfTarget] = useState<string>('');
  const [pfPeriod, setPfPeriod] = useState('');
  const [pfEndDate, setPfEndDate] = useState('');

  // Best execution
  const [bestExecSummary, setBestExecSummary] = useState<any>(null);
  const [execStartDate, setExecStartDate] = useState(
    () => { const d = fromIso(getTenantToday()); d.setDate(d.getDate() - 30); return toIso(d); }
  );
  const [execEndDate, setExecEndDate] = useState(
    getTenantToday()
  );
  
  // AI Assistant state
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiContext, setAiContext] = useState('');
  const [aiAnswer, setAiAnswer] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState<Array<{question: string, answer: string}>>([]);
  
  const fetchDashboard = useCallback(async () => {
    setError(null);
    try {
      const response = await axios.get('/api/regulatory/dashboard');
      setDashboard(response.data);
    } catch (err: any) {
      console.error('Error fetching dashboard:', err);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait a moment before trying again.');
      } else {
        setError('Failed to load dashboard data.');
      }
    }
  }, []);
  
  const fetchFilings = useCallback(async () => {
    setError(null);
    try {
      const response = await axios.get('/api/regulatory/13f', {
        params: { page: 0, size: 1000 }
      });
      setFilings(response.data.content || response.data);
    } catch (err: any) {
      console.error('Error fetching filings:', err);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded while fetching filings.');
      }
    }
  }, []);
  
  const fetchFormPF = useCallback(async () => {
    try {
      const response = await axios.get('/api/regulatory/form-pf', {
        params: { page: 0, size: 1000 }
      });
      const data = response.data.content || response.data;
      setDashboard(prev => prev ? {...prev, recentFormPFFilings: data} : null);
    } catch (err: any) {
      console.error('Error fetching Form PF:', err);
    }
  }, []);
  
  const fetchFormADV = useCallback(async () => {
    try {
      const response = await axios.get('/api/regulatory/form-adv', {
        params: { page: 0, size: 1000 }
      });
      const data = response.data.content || response.data;
      setDashboard(prev => prev ? {...prev, recentFormADVFilings: data} : null);
    } catch (err: any) {
      console.error('Error fetching Form ADV:', err);
    }
  }, []);
  
  const fetchHoldings = useCallback(async (filingId: number) => {
    try {
      const response = await axios.get(`/api/regulatory/13f/${filingId}/holdings`);
      setHoldings(response.data);
    } catch (err: any) {
      console.error('Error fetching holdings:', err);
    }
  }, []);
  
  const fetchBestExecutionSummary = useCallback(async () => {
    try {
      const response = await axios.get('/api/regulatory/best-execution/summary', {
        params: { startDate: execStartDate, endDate: execEndDate }
      });
      setBestExecSummary(response.data);
    } catch (err: any) {
      console.error('Error fetching best execution summary:', err);
    }
  }, [execStartDate, execEndDate]);

  const fetchPortfolioGroups = useCallback(async () => {
    try {
      const response = await axios.get('/api/portfolio-groups');
      setPfGroups(response.data.content || response.data);
    } catch (error) {
      console.error('Error fetching portfolio groups:', error);
    }
  }, []);

  const fetchPfPortfolios = useCallback(async () => {
    try {
      const response = await axios.get('/api/dimensions/portfolios', {
        params: { page: 0, size: 500 }
      });
      setPfPortfolios(response.data.content || response.data);
    } catch (error) {
      console.error('Error fetching portfolios for Form PF:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);
  
  useEffect(() => {
    if (activeTab === '13f') {
      fetchFilings();
    } else if (activeTab === 'form-pf') {
      fetchFormPF();
      fetchPortfolioGroups();
      fetchPfPortfolios();
    } else if (activeTab === 'form-adv') {
      fetchFormADV();
    } else if (activeTab === 'best-execution') {
      fetchBestExecutionSummary();
    } else if (activeTab === 'dashboard') {
      fetchDashboard();
      // For retail tenants, also fetch Form ADV data for dashboard
      if (isRetail) {
        fetchFormADV();
      }
    }
  }, [activeTab, fetchFilings, fetchFormPF, fetchFormADV, fetchBestExecutionSummary, fetchDashboard, fetchPortfolioGroups, fetchPfPortfolios, isRetail]);
  
  const handleGenerateForm13F = async () => {
    if (!filingPeriod || !periodEndDate) {
      alert('Please enter filing period and end date');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post('/api/regulatory/13f/generate', {
        filingPeriod,
        periodEndDate
      });
      
      if (response.data.success) {
        alert(`Form 13F generated! ${response.data.totalPositions} positions, $${response.data.totalValue}K total value`);
        fetchFilings();
        fetchDashboard();
        setFilingPeriod('');
        setPeriodEndDate('');
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmitFiling = async (filingId: number) => {
    if (!window.confirm('Are you sure you want to submit this filing?')) return;
    
    try {
      await axios.post(`/api/regulatory/13f/${filingId}/submit`);
      alert('Filing submitted successfully');
      fetchFilings();
      fetchDashboard();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Submission failed');
    }
  };
  
  const handleExportXml = async (filingId: number) => {
    try {
      const response = await axios.get(`/api/regulatory/13f/${filingId}/export/xml`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filing = filings.find(f => f.id === filingId);
      link.setAttribute('download', `Form13F_${filing?.filingPeriod || filingId}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Export failed');
    }
  };
  
  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };
  
  const formatCurrency = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    return '$' + num.toLocaleString();
  };
  
  // Chart data for best execution by venue
  const venueChartData = bestExecSummary?.venueStatistics ? {
    labels: bestExecSummary.venueStatistics.map((v: any) => v.venue || 'Unknown'),
    datasets: [{
      label: 'Executions',
      data: bestExecSummary.venueStatistics.map((v: any) => v.count),
      backgroundColor: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'],
      borderWidth: 0
    }]
  } : null;
  
  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Shield className="w-8 h-8 text-indigo-500" />
                Regulatory Compliance Hub
              </h1>
              <p className="text-slate-500 font-medium mt-1">
                  {isRetail
                    ? 'Form ADV registration and compliance reporting for RIAs'
                    : 'SEC Form 13F, PF, ADV, MiFID II reporting and best execution monitoring'}
                </p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1.5 rounded-xl">
                {[
                  { id: 'dashboard', label: 'Overview', icon: '📊' },
                  ...(showInstitutionalForms ? [
                    { id: '13f', label: 'Form 13F', icon: '🏛️' },
                    { id: 'form-pf', label: 'Form PF', icon: '📈' },
                  ] : []),
                  { id: 'form-adv', label: 'Form ADV', icon: '📝' },
                  ...(showInstitutionalForms ? [
                    { id: 'best-execution', label: 'Best Exec', icon: '⚡' },
                    { id: 'mifid', label: 'MiFID II', icon: '🇪🇺' },
                  ] : []),
                  { id: 'ai-assistant', label: 'AI Pilot', icon: '🤖' }
                ].map((tab) => (
                  <button 
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2", 
                      activeTab === tab.id ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    <span>{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {activeTab === 'dashboard' && dashboard && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {showInstitutionalForms ? (
                  <>
                    <StatCard label="13F Filings" value={String(dashboard.total13FFilings)} icon={<FileText className="w-5 h-5" />} color="#3b82f6" />
                    <StatCard label="Pending Review" value={String(dashboard.pending13FFilings)} icon={<AlertCircle className="w-5 h-5" />} color="#f59e0b" />
                    <StatCard label="Best Exec Rate" value={`${dashboard.bestExecutionRate30d?.toFixed(1)}%`} icon={<Activity className="w-5 h-5" />} color="#10b981" />
                    <StatCard label="Health Score" value="99.4%" icon={<Shield className="w-5 h-5" />} color="#8b5cf6" />
                  </>
                ) : (
                  <>
                    <StatCard label="Form ADV Filings" value={String(dashboard.totalFormADVFilings || 0)} icon={<FileText className="w-5 h-5" />} color="#3b82f6" />
                    <StatCard label="Pending Review" value={String(dashboard.pendingFormADVFilings || 0)} icon={<AlertCircle className="w-5 h-5" />} color="#f59e0b" />
                    <StatCard label="Compliance Score" value="99.4%" icon={<CheckCircle2 className="w-5 h-5" />} color="#10b981" />
                    <StatCard label="Health Score" value="99.4%" icon={<Shield className="w-5 h-5" />} color="#8b5cf6" />
                  </>
                )}
              </div>

              <GridRow className="mt-8">
                <Card>
                  <CardTitle>📅 Upcoming Deadlines</CardTitle>
                  {dashboard.upcomingDeadlines?.map((d: any, idx: number) => (
                    <DeadlineCard key={idx} urgent={d.urgent || d.daysRemaining <= 7}>
                      <DeadlineTitle>{d.type}</DeadlineTitle>
                      <DeadlineDate>{d.period} • Due: {d.deadline}</DeadlineDate>
                      <DeadlineDays urgent={d.urgent || d.daysRemaining <= 7}>
                        {d.daysRemaining} {d.daysRemaining === 1 ? 'day' : 'days'} remaining
                        {d.urgent && ' ⚠️'}
                      </DeadlineDays>
                    </DeadlineCard>
                  ))}
                  
                  {(!dashboard.upcomingDeadlines || dashboard.upcomingDeadlines.length === 0) && (
                    <div style={{color: '#94a3b8', textAlign: 'center', padding: '2rem'}}>
                      No upcoming deadlines
                    </div>
                  )}
                </Card>
                
                {showInstitutionalForms ? (
                  <Card>
                    <CardTitle>📋 Recent 13F Filings</CardTitle>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Period</Th>
                          <Th>Status</Th>
                          <Th>Positions</Th>
                          <Th>Value ($K)</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.recent13FFilings?.map(f => (
                          <tr key={f.id}>
                            <Td>{f.filingPeriod}</Td>
                            <Td><Badge type={f.status}>{f.status}</Badge></Td>
                            <Td>{f.totalPositions}</Td>
                            <Td>{formatCurrency(f.totalValue)}</Td>
                          </tr>
                        ))}
                        {(!dashboard.recent13FFilings || dashboard.recent13FFilings.length === 0) && (
                          <tr>
                            <Td colSpan={4} style={{textAlign: 'center', color: '#94a3b8'}}>
                              No filings yet
                            </Td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card>
                ) : (
                  <Card>
                    <CardTitle>📋 Recent Form ADV Filings</CardTitle>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Type</Th>
                          <Th>Status</Th>
                          <Th>Filing Date</Th>
                          <Th>AUM</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.recentFormADVFilings?.map((f: any) => (
                          <tr key={f.id}>
                            <Td>{f.filingType}</Td>
                            <Td><Badge type={f.status}>{f.status}</Badge></Td>
                            <Td>{f.filingDate || 'Draft'}</Td>
                            <Td>${((f.regulatoryAum || 0) / 1000000).toFixed(2)}M</Td>
                          </tr>
                        ))}
                        {(!dashboard.recentFormADVFilings || dashboard.recentFormADVFilings.length === 0) && (
                          <tr>
                            <Td colSpan={4} style={{textAlign: 'center', color: '#94a3b8'}}>
                              No filings yet
                            </Td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card>
                )}
                
                {/* Overdue Filings Card */}
                {dashboard.overdueFilings && dashboard.overdueFilings.length > 0 && (
                  <Card style={{background: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444'}}>
                    <CardTitle style={{color: '#f87171'}}>🚨 Overdue Filings</CardTitle>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Type</Th>
                          <Th>Period</Th>
                          <Th>Deadline</Th>
                          <Th>Days Overdue</Th>
                          <Th>Actions</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.overdueFilings.map((f: any, idx: number) => (
                          <tr key={idx}>
                            <Td>{f.type}</Td>
                            <Td>{f.period}</Td>
                            <Td>{f.deadline}</Td>
                            <Td style={{color: '#f87171', fontWeight: '600'}}>{f.daysOverdue} days</Td>
                            <Td>
                              <ActionButton onClick={() => {
                                if (f.type === 'Form 13F') {
                                  setActiveTab('13f');
                                } else if (f.type === 'Form PF') {
                                  setActiveTab('form-pf');
                                } else if (f.type === 'Form ADV') {
                                  setActiveTab('form-adv');
                                }
                              }}>
                                View Filing
                              </ActionButton>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card>
                )}
              </GridRow>
            </>
          )}
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === '13f' && (
            <motion.div key="13f" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Card>
                <CardTitle>🆕 Generate New Form 13F</CardTitle>
                <GridRow>
                  <FormGroup>
                    <Label>Filing Period (e.g., Q4-2024)</Label>
                    <Input
                      value={filingPeriod}
                      onChange={(e) => setFilingPeriod(e.target.value)}
                      placeholder="Q4-2024"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Period End Date</Label>
                    <DateField
                      value={periodEndDate ?? ''}
                      onChange={iso => setPeriodEndDate(iso)}
                    />
                  </FormGroup>
                </GridRow>
                {canGenerate ? (
                  <Button onClick={handleGenerateForm13F} disabled={loading}>
                    {loading ? 'Generating...' : '📄 Generate Form 13F from Current Holdings'}
                  </Button>
                ) : (
                  <Button disabled style={{ opacity: 0.5 }}>
                    {isReadOnly ? 'Read-only View - Generation Restricted' : 'Generation Restricted'}
                  </Button>
                )}
              </Card>
              
              <Card>
                <CardTitle>📁 All 13F Filings</CardTitle>
                <Table>
                  <thead>
                    <tr>
                      <Th>ID</Th>
                      <Th>Period</Th>
                      <Th>End Date</Th>
                      <Th>Status</Th>
                      <Th>Positions</Th>
                      <Th>Value ($K)</Th>
                      <Th>Created</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filings
                      .filter(f => isReadOnly ? f.status !== 'DRAFT' : true)
                      .map(f => (
                      <tr key={f.id}>
                        <Td>{f.id}</Td>
                        <Td style={{fontWeight: 600}}>{f.filingPeriod}</Td>
                        <Td>{f.periodEndDate}</Td>
                        <Td><Badge type={f.status}>{f.status}</Badge></Td>
                        <Td>{f.totalPositions}</Td>
                        <Td>{formatCurrency(f.totalValue)}</Td>
                        <Td>{new Date(f.createdAt).toLocaleDateString()}</Td>
                        <Td>
                          <ActionButton onClick={() => {
                            setSelectedFiling(f);
                            fetchHoldings(f.id);
                          }}>
                            View Holdings
                          </ActionButton>
                          {f.status === 'DRAFT' && canGenerate && (
                            <ActionButton onClick={() => handleSubmitFiling(f.id)}>
                              Submit
                            </ActionButton>
                          )}
                          <ActionButton onClick={() => {
                            window.open(`/api/regulatory/13f/${f.id}/export/xml-enhanced`, '_blank');
                          }}>
                            Export XML
                          </ActionButton>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
              
              {selectedFiling && (
                <Card>
                  <CardTitle>📊 Holdings for {selectedFiling.filingPeriod}</CardTitle>
                  <Table>
                    <thead>
                      <tr>
                        <Th>CUSIP</Th>
                        <Th>Issuer Name</Th>
                        <Th>Class</Th>
                        <Th>Value ($K)</Th>
                        <Th>Shares</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {holdings.map(h => (
                        <tr key={h.id}>
                          <Td style={{fontFamily: 'monospace'}}>{h.cusip}</Td>
                          <Td>{h.issuerName}</Td>
                          <Td>{h.classTitle}</Td>
                          <Td>{formatCurrency(h.value)}</Td>
                          <Td>{formatNumber(h.sharesOrPrincipal)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Button variant="secondary" onClick={() => setSelectedFiling(null)} style={{marginTop: '1rem'}}>
                    Close Holdings
                  </Button>
                </Card>
              )}
            </motion.div>
          )}

          {activeTab === 'form-pf' && (
            <motion.div key="form-pf" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Card>
                <CardTitle>📊 Form PF - Private Fund Reporting</CardTitle>
                {canGenerate && (
                  <div style={{marginBottom: '1.5rem'}}>
                    <GridRow>
                      <FormGroup>
                        <Label>Target Fund</Label>
                        <Select
                          value={selectedPfTarget}
                          onChange={(e) => setSelectedPfTarget(e.target.value)}
                        >
                          <option value="">Select a fund...</option>
                          {pfGroups.map(g => (
                            <option key={`group-${g.id}`} value={`group:${g.id}`}>
                              {g.groupName || g.name} (Group)
                            </option>
                          ))}
                          {pfPortfolios.map(p => (
                            <option key={`portfolio-${p.id}`} value={`portfolio:${p.id}`}>
                              {p.portfolioName || p.name || p.portfolioCode || p.code} (Portfolio)
                            </option>
                          ))}
                        </Select>
                      </FormGroup>
                      <FormGroup>
                        <Label>Filing Period (e.g., Q1-2024)</Label>
                        <Input 
                          value={pfPeriod} 
                          onChange={(e) => setPfPeriod(e.target.value)}
                          placeholder="Q1-2024"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Period End Date</Label>
                        <DateField
                          value={pfEndDate ?? ''}
                          onChange={iso => setPfEndDate(iso)}
                        />
                      </FormGroup>
                    </GridRow>
                    <Button 
                      onClick={async () => {
                        if (!selectedPfTarget || !pfPeriod || !pfEndDate) {
                          alert('Please select a fund, period, and end date');
                          return;
                        }
                        setLoading(true);
                        try {
                          const [targetType, targetId] = selectedPfTarget.split(':');
                          const response = await axios.post('/api/regulatory/form-pf/generate', {
                            ...(targetType === 'group'
                              ? { portfolioGroupId: targetId }
                              : { portfolioId: targetId }),
                            filingPeriod: pfPeriod,
                            periodEndDate: pfEndDate,
                            filingType: 'QUARTERLY'
                          });
                          alert('Form PF generated successfully! ID: ' + response.data.id);
                          fetchFormPF();
                          fetchDashboard();
                        } catch (error: any) {
                          alert('Error: ' + (error.response?.data?.error || error.message));
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                    >
                      {loading ? 'Generating...' : '➕ Generate Institutional Form PF'}
                    </Button>
                  </div>
                )}
                
                <Table>
                  <thead>
                    <tr>
                      <Th>Period</Th>
                      <Th>End Date</Th>
                      <Th>Type</Th>
                      <Th>Gross AUM</Th>
                      <Th>Net AUM</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard?.recentFormPFFilings?.map((f: any) => (
                      <tr key={f.id}>
                        <Td>{f.filingPeriod}</Td>
                        <Td>{f.periodEndDate}</Td>
                        <Td>{f.filingType}</Td>
                        <Td>${(f.grossAssetValue / 1000000).toFixed(2)}M</Td>
                        <Td>${(f.netAssetValue / 1000000).toFixed(2)}M</Td>
                        <Td><Badge type={f.status}>{f.status}</Badge></Td>
                        <Td>
                          <ActionButton onClick={() => window.open(`/api/regulatory/form-pf/${f.id}/export/xml`, '_blank')}>
                            📥 Export XML
                          </ActionButton>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            </motion.div>
          )}

          {activeTab === 'form-adv' && (
            <motion.div key="form-adv" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Card>
                <CardTitle>📝 Form ADV - Investment Adviser Registration</CardTitle>
                {canGenerate && (
                  <div style={{marginBottom: '1.5rem'}}>
                    <Button 
                      onClick={async () => {
                        try {
                          const response = await axios.post('/api/regulatory/form-adv/generate', {
                            filingType: 'ANNUAL'
                          });
                          alert('Form ADV generated successfully! ID: ' + response.data.id);
                          fetchDashboard();
                        } catch (error: any) {
                          alert('Error: ' + (error.response?.data?.error || error.message));
                        }
                      }}
                    >
                      ➕ Generate Form ADV
                    </Button>
                  </div>
                )}
                
                <Table>
                  <thead>
                    <tr>
                      <Th>Type</Th>
                      <Th>Filing Date</Th>
                      <Th>Regulatory AUM</Th>
                      <Th>Total Accounts</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard?.recentFormADVFilings?.map((f: any) => (
                      <tr key={f.id}>
                        <Td>{f.filingType}</Td>
                        <Td>{f.filingDate || 'Draft'}</Td>
                        <Td>${(f.regulatoryAum / 1000000).toFixed(2)}M</Td>
                        <Td>{f.totalAccounts}</Td>
                        <Td><Badge type={f.status}>{f.status}</Badge></Td>
                        <Td>
                          <ActionButton onClick={() => window.open(`/api/regulatory/form-adv/${f.id}/export/xml`, '_blank')}>
                            📥 Export XML
                          </ActionButton>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            </motion.div>
          )}

          {activeTab === 'best-execution' && (
            <motion.div key="best-exec" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Card>
                <CardTitle>📅 Select Date Range</CardTitle>
                <GridRow>
                  <FormGroup>
                    <Label>Start Date</Label>
                    <DateField value={execStartDate ?? ''} onChange={iso => setExecStartDate(iso)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>End Date</Label>
                    <DateField value={execEndDate ?? ''} onChange={iso => setExecEndDate(iso)} />
                  </FormGroup>
                </GridRow>
                <div style={{display: 'flex', gap: '12px'}}>
                  <Button onClick={fetchBestExecutionSummary}>🔄 Refresh Summary</Button>
                </div>
              </Card>

              {bestExecSummary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard 
                      label="Total Executions" 
                      value={formatNumber(bestExecSummary.totalExecutions)} 
                      icon={<Activity className="w-5 h-5" />} 
                      color="#3b82f6" 
                    />
                    <StatCard 
                      label="Best Exec Achieved" 
                      value={formatNumber(bestExecSummary.bestExecutionAchieved)} 
                      icon={<CheckCircle2 className="w-5 h-5" />} 
                      color="#10b981" 
                    />
                    <StatCard 
                      label="Best Exec Rate" 
                      value={`${bestExecSummary.bestExecutionRate?.toFixed(1)}%`} 
                      icon={<Zap className="w-5 h-5" />} 
                      color="#8b5cf6" 
                    />
                    <StatCard 
                      label="Avg Price Improvement" 
                      value={bestExecSummary.averagePriceImprovement ? bestExecSummary.averagePriceImprovement.toFixed(4) + '%' : '-'} 
                      icon={<BarChart3 className="w-5 h-5" />} 
                      color="#f59e0b" 
                    />
                  </div>
                  
                  <GridRow>
                    <Card>
                      <CardTitle>📊 Executions by Venue</CardTitle>
                      {venueChartData ? (
                        <ChartContainer>
                          <Doughnut data={venueChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { color: '#94a3b8' } } } }} />
                        </ChartContainer>
                      ) : (
                        <div style={{textAlign: 'center', color: '#94a3b8', padding: '2rem'}}>No execution data available</div>
                      )}
                    </Card>
                    <Card>
                      <CardTitle>📋 Venue Statistics</CardTitle>
                      <Table>
                        <thead>
                          <tr>
                            <Th>Venue</Th>
                            <Th>Count</Th>
                            <Th>Avg Price Imp</Th>
                            <Th>Fill Rate</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {bestExecSummary.venueStatistics?.map((v: any, idx: number) => (
                            <tr key={idx}>
                              <Td>{v.venue || 'Unknown'}</Td>
                              <Td>{v.count}</Td>
                              <Td>{v.avgPriceImprovement?.toFixed(4) || '-'}%</Td>
                              <Td>{v.avgFillRate?.toFixed(1) || '-'}%</Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card>
                  </GridRow>
                </>
              )}
            </motion.div>
          )}

          {activeTab === 'mifid' && (
            <motion.div key="mifid" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Card>
                <CardTitle>🇪🇺 MiFID II Transaction Reporting</CardTitle>
                <div style={{color: '#94a3b8', textAlign: 'center', padding: '3rem'}}>
                  <div style={{fontSize: '3rem', marginBottom: '1rem'}}>📋</div>
                  <h3 style={{color: '#f1f5f9', marginBottom: '1rem'}}>MiFID II Transaction Reporting</h3>
                  <p>MiFID II reporting is automatically generated when trades are executed.</p>
                  <div style={{marginTop: '2rem'}}>
                    <Button variant="secondary">📥 Export Unreported Transactions</Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {activeTab === 'ai-assistant' && (
            <motion.div key="ai" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <Card>
                <CardTitle>🤖 AI Compliance Assistant</CardTitle>
                <FormGroup>
                  <Label>Compliance Question</Label>
                  <textarea
                    value={aiQuestion}
                    onChange={(e) => setAiQuestion(e.target.value)}
                    className="w-full bg-slate-900 border border-white/10 rounded-xl p-4 text-white min-h-[100px]"
                    placeholder="Ask about Form 13F thresholds, Form PF filing requirements, etc."
                  />
                </FormGroup>
                <Button 
                  onClick={async () => {
                    if (!aiQuestion.trim()) return;
                    setAiLoading(true);
                    try {
                      const response = await axios.post('/api/ai/regulatory/ask', { question: aiQuestion, context: aiContext });
                      setAiAnswer(response.data);
                      setAiHistory(prev => [...prev, { question: aiQuestion, answer: response.data.answer || response.data.response }]);
                    } catch (error) {
                      setAiAnswer({ error: 'Failed to get answer' });
                    } finally {
                      setAiLoading(false);
                    }
                  }}
                  disabled={aiLoading}
                >
                  {aiLoading ? '🔄 Thinking...' : 'Ask AI'}
                </Button>
              </Card>

              {aiAnswer && (
                <Card className="bg-indigo-500/5 border-indigo-500/20">
                  <CardTitle className="text-indigo-400">🤖 AI Response</CardTitle>
                  <div className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                    {aiAnswer.answer || aiAnswer.response || aiAnswer.error}
                  </div>
                </Card>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Container>
  );
};

export default RegulatoryReportingScreen;
